// src/theme.js
import { createTheme } from '@mui/material/styles';

// Blue brand color palette from the design system
const bluePalette = {
  B50: '#EEF6FF',   // Pressed down states of interactive active elements
  B100: '#8EC2FF',  // Default color for active elements
  B400: '#378BE5',  // Hover states of interactive active elements
  B500: '#1070CA',  // Brand color - primary color for all CTAs
  BA500: 'rgba(16, 112, 202, 0.4)', // Disabled state of primary buttons (40% alpha)
  B600: '#005DAD',  // Hover and selected state for primary button
  B700: '#004482',  // Selected State of primary button
};

const theme = createTheme({
  palette: {
    primary: {
      main: bluePalette.B500,      // #1070CA - Primary brand color
      light: bluePalette.B100,     // #8EC2FF - Light blue for active elements
      dark: bluePalette.B600,      // #005DAD - Dark blue for hover states
      contrastText: '#ffffff',
    },
    secondary: {
      main: bluePalette.B400,      // #378BE5 - Secondary blue
      light: bluePalette.B50,      // #EEF6FF - Very light blue
      dark: bluePalette.B700,      // #004482 - Dark blue
      contrastText: '#ffffff',
    },
    info: {
      main: bluePalette.B100,      // #8EC2FF - Info blue
      light: bluePalette.B50,      // #EEF6FF - Light info
      dark: bluePalette.B400,      // #378BE5 - Dark info
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#000000',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: {
      fontWeight: 600,
      color: '#717171',
    },
    h5: {
      fontWeight: 600,
      color: '#717171',
    },
    h6: {
      fontWeight: 600,
      color: '#717171',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#717171',
    },
    subtitle2: {
      fontWeight: 500,
      color: '#717171',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 2px 8px rgba(16, 112, 202, 0.2)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(16, 112, 202, 0.3)',
          },
        },
        containedPrimary: {
          backgroundColor: bluePalette.B500,
          '&:hover': {
            backgroundColor: bluePalette.B600,
          },
          '&:active': {
            backgroundColor: bluePalette.B700,
          },
          '&:disabled': {
            backgroundColor: bluePalette.BA500,
            color: 'rgba(255, 255, 255, 0.6)',
          },
        },
        containedSecondary: {
          backgroundColor: bluePalette.B100,
          color: '#000000',
          '&:hover': {
            backgroundColor: bluePalette.B400,
            color: '#ffffff',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${bluePalette.B50}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
        },
        elevation2: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: bluePalette.B50,
          color: '#000000',
          border: `1px solid ${bluePalette.B100}`,
          '&:hover': {
            backgroundColor: bluePalette.B100,
            color: '#000000',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: bluePalette.B500,
          boxShadow: '0 2px 8px rgba(16, 112, 202, 0.2)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: bluePalette.B50,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: bluePalette.B100,
            },
            '&.Mui-focused fieldset': {
              borderColor: bluePalette.B500,
            },
          },
        },
      },
    },
  },
});

export default theme;
